import React, { useState, useEffect, useMemo } from "react";
import { useRef } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./style.css";
import useFetchProfile from "../../hooks/useFetchProfile";
import logo from "../../assets/images/logo.png";
import cornerPng from "../../assets/images/corner.png";
import logoPng from "../../assets/images/logo.png";
// import ciaaPng from "../../assets/images/ciaa.png";
// import iaoPng from "../../assets/images/iao.png";
import signaturePng from "../../assets/images/signature.png";

import useSubjectsFetch from "../../hooks/useFetchSubjects";
import axios from "axios";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import html2canvas from "html2canvas";

function Certificate({
  isOpen,
  onClose,
  subjects,
  auth,
  setCommonWarning,
  navigate,
}) {
  const [certificateData, setCertificateData] = useState([]);
  const [certificateDataCompletedTopicsCount, setCertificateDataCompletedTopicsCount] = useState(0);
  const [lastAttemptDate, setLastAttemptDate] = useState("");
  const certificateRef = useRef(null);

  const purchased_subs = useMemo(
    () =>
      subjects.filter((item) => item.is_purchased === 1).map((item) => item.id),
    [subjects]
  );

  const generateReportPDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    let y = 0;

    // ========== PAGE 1: CERTIFICATE COVER ==========
    doc.setFillColor(168, 85, 247);
    doc.rect(0, 0, pageWidth, pageHeight * 0.5, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("CERTIFICATE", margin, 50);
    doc.text("OF EXCELLENCE", margin, 75);

    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("FEFDY Brain Gym", margin, 100);

    // Bottom section
    doc.setFillColor(255, 255, 255);
    doc.rect(0, pageHeight * 0.5, pageWidth, pageHeight * 0.5, "F");

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Achievement Report", margin, pageHeight * 0.55 + 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Generated: ${new Date().toLocaleDateString()}`,
      margin,
      pageHeight * 0.55 + 40
    );

    // ========== PAGE 2: CONTENT ==========
    doc.addPage();
    y = margin;

    // console.log("certificateData", certificateData);

    if (!certificateData || certificateData.length === 0) {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(14);
      doc.setFont("helvetica", "italic");
      doc.text("No data available to display.", margin, y);
    } else {
      certificateData.forEach((subject) => {
        // Filter levels: only include levels where ALL topics have non-zero subtopics
        const validLevels = subject.levels?.filter((level) => {
          // Check if level has topics
          if (!level.topics || level.topics.length === 0) return false;

          // Check each topic in the level
          return level.topics.every((topic) => {
            // Check if topic has subtopics data
            if (!topic.subtopics_data || topic.subtopics_data.length === 0)
              return false;

            // Check if ALL subtopics in this topic have percent !== 0
            // Using .some() with condition percent !== 0 will check if at least one has non-zero
            // But we want ALL to be non-zero, so we check if any has 0
            const hasZeroPercent = topic.subtopics_data.some(
              (sub) => sub.percent === 0
            );

            // If any subtopic has 0%, exclude this topic (and thus this level)
            return !hasZeroPercent;
          });
        });

        // If no valid levels for this subject, skip the entire subject
        if (!validLevels || validLevels.length === 0) {
          return; // Skip to next subject
        }

        // Subject header
        if (y > pageHeight - margin - 50) {
          doc.addPage();
          y = margin;
        }
        doc.setTextColor(168, 85, 247);
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.text(subject.name, margin, y);
        y += 12;

        doc.setDrawColor(168, 85, 247);
        doc.setLineWidth(2);
        doc.line(margin, y, margin + 80, y);
        y += 15;

        // Process only valid levels
        validLevels.forEach((level) => {
          // Check if level has valid topics (should already be filtered, but double-check)
          if (!level.topics || level.topics.length === 0) {
            return;
          }

          if (y > pageHeight - margin - 50) {
            doc.addPage();
            y = margin;
          }

          doc.setTextColor(100, 100, 100);
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(`Level: ${level.name}`, margin, y);
          y += 8;

          level.topics.forEach((topic) => {
            // Filter out subtopics with 0% (just in case)
            const validSubtopics = topic.subtopics_data?.filter(
              (sub) => sub.percent !== 0
            );

            if (!validSubtopics || validSubtopics.length === 0) {
              // This shouldn't happen due to our filtering, but just in case
              return;
            }

            if (y > pageHeight - margin - 50) {
              doc.addPage();
              y = margin;
            }

            doc.setTextColor(80, 80, 80);
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(`Topic: ${topic.name}`, margin + 5, y);
            y += 7;

            const tableData = validSubtopics.map((sub) => [
              sub.title,
              sub.percent,
            ]);

            doc.autoTable({
              startY: y,
              head: [["Subtopic Name", "Mark"]],
              body: tableData,
              theme: "striped",
              headStyles: {
                fillColor: [168, 85, 247],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 10,
                padding: 6,
              },
              bodyStyles: { fontSize: 9, padding: 5, textColor: [50, 50, 50] },
              alternateRowStyles: { fillColor: [245, 245, 250] },
              columnStyles: {
                0: { cellWidth: contentWidth * 0.65 },
                1: { cellWidth: contentWidth * 0.35, halign: "center" },
              },
              margin: { left: margin, right: margin },
            });

            y = doc.lastAutoTable.finalY + 10;
          });
        });
      });
    }

    // ========== FOOTER ON ALL PAGES ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFillColor(245, 245, 250);
      doc.rect(0, pageHeight - 12, pageWidth, 12, "F");

      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.setFont("helvetica", "normal");
      doc.text(`© 2025 FEFDY Brain Gym`, margin, pageHeight - 6);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 20,
        pageHeight - 6
      );
    }

    // Save PDF
    return doc.output("blob");
  };

  const generateCertificatePDF = async () => {
    const element = certificateRef.current;
    if (!element) return null;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const yOffset = imgHeight < pdfHeight ? (pdfHeight - imgHeight) / 2 : 0;

    pdf.addImage(imgData, "PNG", 0, yOffset, imgWidth, imgHeight);

    return pdf.output("blob");
  };

  const handleDownloadAllPDFs = async () => {
    const zip = new JSZip();

    const certificateBlob = await generateCertificatePDF();
    const reportBlob = await generateReportPDF();

    zip.file("Certificate.pdf", certificateBlob);
    zip.file("Certificate Report.pdf", reportBlob);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, "FEFDY-Certificate-Report.zip");
  };

  useEffect(() => {
    const loadCertificateData = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}certificate`,
          {
            token: auth.token,
            subjects: purchased_subs,
          }
        );

        if (res.status === 200) {
          // console.log("res.data.last_test_date", res.data);
          setCertificateData(res.data.data);
          const certificateData123 = res.data.data;
          const completedSubjects = certificateData123
            .map((sub) => {
              const completedLevels = sub.levels
                .filter((level) => level.is_completed === 1)
                .map((level) => level.name);

              return completedLevels.length
                ? { subject: sub.name, levels: completedLevels }
                : null;
            })
            .filter(Boolean);
          setCertificateDataCompletedTopicsCount(completedSubjects.length);
          // console.log("certificateDataCompletedTopicsCount", completedSubjects.length);
          setLastAttemptDate(res.data.last_test_date);
        } else if (res.status === 400) {
          setCommonWarning(res.data.message || "Bad request");
        } else if (res.status === 401) {
          navigate("/login");
        }
      } catch (error) {
        console.error("Error fetching certificate:", error);
        setCommonWarning("Something went wrong while fetching certificate.");
      }
    };

    if (purchased_subs.length > 0 && auth?.token) {
      loadCertificateData();
    }
  }, [purchased_subs, auth, setCommonWarning, navigate]);

  if (!isOpen) return null;
  // console.log("certificateData", certificateData)

  // const certificateData = [
  //   {
  //     "id": 1,
  //     "name": "Science",
  //     "is_completed": 0,
  //     "is_purchased": 1,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 1,
  //         "topics": [
  //           {
  //             "topic_id": 1,
  //             "name": "Animals",
  //             "is_completed": 0,
  //             "subtopics_data": [
  //               {
  //                 "id": 1,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 2,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animals sounds",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 3,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Baby animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 4,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Pet animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 5,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Wild animals",
  //                 "is_completed": 0
  //               },
  //               {
  //                 "id": 6,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Big and small animals",
  //                 "is_completed": 0
  //               },
  //               {
  //                 "id": 7,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animal homes",
  //                 "is_completed": 0
  //               },
  //               {
  //                 "id": 8,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animal moves",
  //                 "is_completed": 0
  //               }
  //             ]
  //           }
  //         ]
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 1,
  //         "is_purchased": 1,
  //         "topics": [
  //           {
  //             "topic_id": 2,
  //             "name": "Animals",
  //             "is_completed": 1,
  //             "subtopics_data": [
  //               {
  //                 "id": 9,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 10,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animal sounds",
  //                 "is_completed": 1
  //               }
  //             ]
  //           }
  //         ]
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   },
  //   {
  //     "id": 2,
  //     "name": "Mathematics",
  //     "is_completed": 0,
  //     "is_purchased": 1,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 1,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   },
  //   {
  //     "id": 3,
  //     "name": "English",
  //     "is_completed": 0,
  //     "is_purchased": 0,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   }
  // ];

  // const certificateData = [
  //   {
  //     "id": 1,
  //     "name": "Science",
  //     "is_completed": 0,
  //     "is_purchased": 1,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 1,
  //         "topics": [
  //           {
  //             "topic_id": 1,
  //             "name": "Animals",
  //             "is_completed": 0,
  //             "subtopics_data": [
  //               {
  //                 "id": 1,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animals",
  //                 "is_completed": 0
  //               },
  //               {
  //                 "id": 2,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Animals sounds",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 3,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Baby animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 4,
  //                 "subject": 1,
  //                 "category": "[\"1\",\"2\"]",
  //                 "title": "Pet animals",
  //                 "is_completed": 1
  //               },
  //               {
  //                 "id": 5,
  //                 "subject": 1,
  //                 "category": "[\"2\"]",
  //                 "title": "Domestic Animals",
  //                 "is_completed": 0
  //               }
  //             ]
  //           }
  //         ]
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 0,
  //         "is_purchased": 1,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   },
  //   {
  //     "id": 2,
  //     "name": "Mathematics",
  //     "is_completed": 0,
  //     "is_purchased": 1,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 1,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   },
  //   {
  //     "id": 3,
  //     "name": "English",
  //     "is_completed": 0,
  //     "is_purchased": 0,
  //     "levels": [
  //       {
  //         "level_id": 1,
  //         "name": "Explorer",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 2,
  //         "name": "Beginner",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 3,
  //         "name": "LEARNER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 4,
  //         "name": "THINKER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 5,
  //         "name": "CHALLENGER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 6,
  //         "name": "ACHIEVER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 7,
  //         "name": "MASTER",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       },
  //       {
  //         "level_id": 8,
  //         "name": "GENIUS",
  //         "is_completed": 0,
  //         "is_purchased": 0,
  //         "topics": []
  //       }
  //     ]
  //   }
  // ];

  let allPurchasedSubjects = true;
  let allCompletedSubjects = true;
  let allPurchasedLevels = true;
  let allCompletedLevels = true;

  // To track completed levels per subject
  let completedLevelsPerSubject = [];

  certificateData.forEach((subject) => {
    if (subject.is_purchased !== 1) allPurchasedSubjects = false;
    if (subject.is_completed !== 1) allCompletedSubjects = false;

    const levels = subject.levels;
    let completedLevels = [];

    levels.forEach((level) => {
      if (level.is_purchased !== 1) allPurchasedLevels = false;
      if (level.is_completed !== 1) allCompletedLevels = false;

      if (level.is_completed === 1) completedLevels.push(level.name);
    });

    completedLevelsPerSubject.push(completedLevels);
  });

  // console.log("allPurchasedSubjects", allPurchasedSubjects);
  // console.log("allCompletedSubjects", allCompletedSubjects);
  // console.log("allPurchasedLevels", allPurchasedLevels);
  // console.log("allCompletedLevels", allCompletedLevels);
  // console.log("completedLevelsPerSubject", completedLevelsPerSubject);

  let caseOne = false;
  let caseTwo = false;
  let caseThree = false;
  let caseFour = false;

  // Case 1: All subjects and levels purchased & completed
  if (
    allPurchasedSubjects &&
    allCompletedSubjects &&
    allPurchasedLevels &&
    allCompletedLevels
  ) {
    caseOne = true;
  } else {
    // Case 3: Same completed levels across all purchased subjects
    const firstSubjectLevels = completedLevelsPerSubject[0] || [];
    const sameLevelsAcrossSubjects = completedLevelsPerSubject.every(
      (levels) =>
        levels.length === firstSubjectLevels.length &&
        levels.every((level) => firstSubjectLevels.includes(level))
    );

    if (firstSubjectLevels.length > 0 && sameLevelsAcrossSubjects) {
      caseThree = true;
    }
    // Case 2: Some levels completed across subjects
    else if (completedLevelsPerSubject.some((levels) => levels.length > 0)) {
      caseTwo = true;
    }
    // Case 4: Nothing completed
    else {
      caseFour = true;
    }
  }

  const calculatedAge = calculateAge(auth.user.dob);

  return (
    <div className="certificate-modal-overlay" onClick={onClose}>
      <div
        className="certificate-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose}>
          ✕
        </button>

        {/* Certificate Content */}
        <div
          className={`certificate ${caseFour ? "certificate-blurred" : ""}`}
          ref={certificateRef}
        >
          <div className="inner-border">
            {/* Corners */}
            <img src={cornerPng} className="corner tl" alt="corner" />
            <img src={cornerPng} className="corner tr" alt="corner" />
            <img src={cornerPng} className="corner bl" alt="corner" />
            <img src={cornerPng} className="corner br" alt="corner" />

            {/* Logo */}
            <div className="logo">
              <img src={logoPng} className="logo-main" alt="FEFDY Logo" />
            </div>

            {/* Header */}
            <div className="certificate-header">
              {/* <img src={ciaaPng} className="logo_1" alt="Left Logo" /> */}

              <div className="title-text">
                <h1 className="certificate-title">CERTIFICATE</h1>
                <h2 className="certificate-subtitle">OF EXCELLENCE</h2>
              </div>

              {/* <img src={iaoPng} className="logo_1" alt="Right Logo" /> */}
            </div>

            {/* Content */}
            {/* <p className="presented">
              THIS CERTIFICATE IS PROUDLY PRESENTED TO
            </p> */}

            <p className="description" style={{ whiteSpace: "pre-line" }}>
              {/* <span className="recipient-inline">Thanikachalam Venkataramanan</span>
              , Age: <span className="recipient-inline">24</span>, for completing
              the following subject in the <strong>FEFDY Brain Gym</strong> with
              dedication: Science - C1 (L1 - L3), C2 (L1), C3 (-), Mathematics - C1
              (L1), C2 (-), C3 (-), English - C1 (-). This achievement is recognized
              and appreciated throughout the year{" "}
              <span className="recipient-inline">2025</span>. */}
              {caseOne && (
                <span>
                  This certificate is proudly presented to{" "}
                  <span className="recipient-inline">{auth.user.name}</span>,
                  Age <span className="recipient-inline">{calculatedAge}</span>,
                  for successfully completing all eight learning levels
                  (Explorer, Beginner, Learner, Thinker, Challenger, Achiever,
                  Master, and Genius) in the subjects{" "}
                  {certificateData.map((sub) => sub.name).join(", ")} at the
                  Fefdy Brain Gym. Your dedication and hard work throughout
                  every level are truly commendable. This achievement is proudly
                  recognized throughout{" "}
                  <span className="recipient-inline">
                    {new Date().getFullYear()}
                  </span>
                  .
                </span>
              )}

              {caseTwo && (
                <span>
                  This certificate is proudly presented to{" "}
                  <span className="recipient-inline">{auth.user.name}</span>,
                  Age <span className="recipient-inline">{calculatedAge}</span>,
                  for completing multiple levels {certificateDataCompletedTopicsCount > 1 ? `across the subjects:` : `in the subject:`}
                  {"\n"}
                  {certificateData
                    .map((sub) => {
                      const completedLevels = sub.levels
                        .filter((level) => level.is_completed === 1)
                        .map((level) => level.name)
                        .join(", ");
                      // Only show the subject if there is at least one completed level
                      return completedLevels
                        ? `${sub.name} – ${completedLevels}`
                        : null;
                    })
                    .filter(Boolean) // Remove nulls for subjects with no completed levels
                    .join("\n")}
                  {"\n"}This achievement is proudly recognized throughout{" "}
                  <span className="recipient-inline">
                    {new Date().getFullYear()}
                  </span>
                  .
                </span>
              )}

              {caseThree && (
                <span>
                  This certificate is proudly presented to{" "}
                  <span className="recipient-inline">{auth.user.name}</span>,
                  Age <span className="recipient-inline">{calculatedAge}</span>,
                  for successfully completing the{" "}
                  {completedLevelsPerSubject[0].join(", ")} level
                  {completedLevelsPerSubject[0].length > 1 ? "s" : ""} in the
                  subjects {certificateData.map((sub) => sub.name).join(", ")}.
                  This achievement is proudly recognized throughout{" "}
                  <span className="recipient-inline">
                    {new Date().getFullYear()}
                  </span>
                  .
                </span>
              )}

              {caseFour && (
                <span>
                  This certificate is proudly presented to{" "}
                  <span className="recipient-inline">{auth.user.name}</span>,
                  Age <span className="recipient-inline">{calculatedAge}</span>,
                  for starting your journey in the FEFDY Brain Gym. No levels
                  have been completed yet, but your dedication to learning is
                  acknowledged. Keep going, and you will achieve great
                  milestones!
                </span>
              )}
            </p>

            {/* Footer */}
            <div className="footer_certificate">
              <div className="footer-box">
                {lastAttemptDate}
                <div className="footer-line"></div>
                <span>DATE</span>
              </div>

              <div className="footer-box">
                <img src={signaturePng} className="signature" alt="Signature" />
                <div className="footer-line"></div>
                <span>FOUNDER OF FEFDY</span>
              </div>
            </div>
          </div>
        </div>
        {caseFour && (
          <div className="certificate-overlay-message">
            <h3>Certificate Not Yet Earned</h3>
            <p>
              No levels have been completed yet, but your dedication to learning
              is acknowledged. Keep going, and you will achieve great
              milestones!
            </p>
          </div>
        )}
        {/* Download Button */}
        {!caseFour && (
          <button
            className="modal-download-btn"
            onClick={handleDownloadAllPDFs}
          ></button>
        )}
      </div>
    </div>
  );
}

function calculateAge(dobString) {
  if (!dobString) return null;
  const dob = new Date(dobString);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

export default Certificate;
